import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ActionItem, ActionItemIconPosition, ActionItemStyle, NavigationLink } from '@stylehaven/components';
import { TaskDashboardConfiguration } from '../../entities/task-dashboard-configuration.entity';
import { TaskSubject } from '../../entities/task-subject.entity';
import { BaseTask } from '../../entities/task.entity';

@Component({
    standalone: false,
    selector: 'digihaven-task-dashboard',
    templateUrl: './task-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskDashboardComponent implements OnInit, OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    readonly configuration$: ReplaySubject<TaskDashboardConfiguration> = new ReplaySubject<TaskDashboardConfiguration>(
        1
    );

    readonly totalTaskCount$: ReplaySubject<number> = new ReplaySubject<number>(1);

    actionItems$!: Observable<ActionItem[]>;

    @Input()
    tasks?: BaseTask<TaskSubject, string>[];

    @Input()
    taskListNavigation!: NavigationLink;

    @Input()
    isLoading = false;

    @Input()
    hasError = false;

    @Input()
    set totalTaskCount(items: number) {
        this.totalTaskCount$.next(items);
    }

    @Input()
    set configuration(configuration: TaskDashboardConfiguration) {
        this.configuration$.next(configuration);
    }

    @Input()
    taskLinks!: NavigationLink[];

    @Output()
    taskSelected: EventEmitter<BaseTask<TaskSubject, string>> = new EventEmitter<BaseTask<TaskSubject, string>>();

    ngOnInit(): void {
        this.initActionItemStream();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.configuration$.complete();
        this.totalTaskCount$.complete();
    }

    handleTaskSelected(task: BaseTask<TaskSubject, string>): void {
        this.taskSelected.emit(task);
    }

    private initActionItemStream(): void {
        this.actionItems$ = combineLatest([this.configuration$, this.totalTaskCount$]).pipe(
            takeUntil(this.destroy$),
            map(([config, taskCount]) => [
                {
                    label: config.navigation.title + this.appendTaskCount(taskCount),
                    icon: 'arrow-right',
                    iconPosition: ActionItemIconPosition.Right,
                    style: ActionItemStyle.Link,
                    navigationLink: this.taskListNavigation
                }
            ])
        );
    }

    private appendTaskCount(taskCount: number | undefined): string {
        return taskCount !== undefined && taskCount > 0 ? ` (${taskCount})` : '';
    }

    getNavigationLink(i: number): NavigationLink {
        return this.taskLinks[i];
    }
}
